import { useCallback, useState } from "react";
import {
  CART_TYPE_STORAGE_KEY,
  DEFAULT_CART_TYPE,
} from "@/shared/constants/cart.constants";
import type { CartType } from "@/shared/types/unified-cart.type";

/**
 * Validate that a value is a valid cart type
 */
const isValidCartType = (value: unknown): value is CartType =>
  value === "boutique" || value === "deals" || value === "rentals";

/**
 * Get the initial cart type from localStorage
 */
const getInitialCartType = (): CartType => {
  try {
    const stored = localStorage.getItem(CART_TYPE_STORAGE_KEY);
    if (stored && isValidCartType(stored)) {
      return stored;
    }
  }
  catch {
    // localStorage not available
  }
  return DEFAULT_CART_TYPE;
};

/**
 * Hook to manage the active cart type selection
 * Persists the selection in localStorage
 */
export const useCartSelector = (): [CartType, (type: CartType) => void] => {
  const [activeType, setActiveType] = useState<CartType>(getInitialCartType);

  const setActiveCartType = useCallback((type: CartType) => {
    try {
      localStorage.setItem(CART_TYPE_STORAGE_KEY, type);
    }
    catch {
      // localStorage not available
    }
    setActiveType(type);
  }, []);

  return [activeType, setActiveCartType];
};
